import { useState } from 'react'
import { Icon } from '@iconify/react'
import type { ChecklistItem as ChecklistItemType, ChecklistSubitem } from '@/types/checklist'
import styles from './ChecklistItem.module.css'

interface ChecklistItemProps {
  item: ChecklistItemType
  onUpdate: (updates: {
    text?: string
    completed?: boolean
    dueDate?: string
    note?: string
  }) => void
  onRemove: () => void
  onMove: (direction: 'up' | 'down') => void
  onAddSubitem: () => void
  onUpdateSubitem: (
    subitemId: string,
    updates: { text?: string; completed?: boolean }
  ) => void
  onRemoveSubitem: (subitemId: string) => void
  onMoveSubitem: (subitemId: string, direction: 'up' | 'down') => void
  canMoveUp: boolean
  canMoveDown: boolean
}

export function ChecklistItem({
  item,
  onUpdate,
  onRemove,
  onMove,
  onAddSubitem,
  onUpdateSubitem,
  onRemoveSubitem,
  onMoveSubitem,
  canMoveUp,
  canMoveDown,
}: ChecklistItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState(item.text)
  const [subitemsExpanded, setSubitemsExpanded] = useState(true)
  const hasSubitems = item.subitems.length > 0

  const handleBlur = () => {
    setIsEditing(false)
    const trimmed = editText.trim()
    if (trimmed !== item.text) {
      onUpdate({ text: trimmed || 'Item' })
    }
    setEditText(item.text)
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    }
    if (e.key === 'Escape') {
      setEditText(item.text)
      setIsEditing(false)
      e.currentTarget.blur()
    }
  }

  return (
    <div className={styles.item}>
      <div className={styles.itemRow}>
        <input
          type="checkbox"
          checked={item.completed}
          onChange={(e) => onUpdate({ completed: e.target.checked })}
          className={styles.checkbox}
          aria-label={item.text || 'Marcar como concluído'}
        />
        <div className={styles.itemContent}>
          {isEditing ? (
            <input
              type="text"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              className={styles.editInput}
              autoFocus
              aria-label="Editar texto do item"
            />
          ) : (
            <span
              className={`${styles.itemText} ${item.completed ? styles.completed : ''}`}
              onClick={() => {
                setIsEditing(true)
                setEditText(item.text)
              }}
              onFocus={() => setIsEditing(true)}
              role="button"
              tabIndex={0}
              aria-label="Editar item"
            >
              {item.text || 'Item sem texto'}
            </span>
          )}
        </div>
        <div className={styles.itemActions}>
          <button
            type="button"
            onClick={() => onMove('up')}
            disabled={!canMoveUp}
            className={styles.iconBtn}
            aria-label="Subir item"
          >
            <Icon icon="mdi:chevron-up" />
          </button>
          <button
            type="button"
            onClick={() => onMove('down')}
            disabled={!canMoveDown}
            className={styles.iconBtn}
            aria-label="Descer item"
          >
            <Icon icon="mdi:chevron-down" />
          </button>
          <button
            type="button"
            onClick={onAddSubitem}
            className={styles.iconBtn}
            aria-label="Adicionar subitem"
            title="Adicionar subitem"
          >
            <Icon icon="mdi:format-list-bulleted" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className={styles.iconBtnDanger}
            aria-label="Remover item"
          >
            <Icon icon="mdi:trash-can-outline" />
          </button>
        </div>
      </div>

      {hasSubitems && (
        <>
          <button
            type="button"
            className={styles.toggleSubitems}
            onClick={() => setSubitemsExpanded((v) => !v)}
            aria-expanded={subitemsExpanded}
          >
            <Icon
              icon={subitemsExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'}
            />
            <span>
              {item.subitems.length} subitem
              {item.subitems.length !== 1 ? 's' : ''}
            </span>
          </button>
          {subitemsExpanded && (
            <ul className={styles.subitems}>
              {item.subitems.map((sub, index) => (
                <SubitemRow
                  key={sub.id}
                  subitem={sub}
                  onUpdate={(updates) => onUpdateSubitem(sub.id, updates)}
                  onRemove={() => onRemoveSubitem(sub.id)}
                  onMove={(dir) => onMoveSubitem(sub.id, dir)}
                  canMoveUp={index > 0}
                  canMoveDown={index < item.subitems.length - 1}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

interface SubitemRowProps {
  subitem: ChecklistSubitem
  onUpdate: (updates: { text?: string; completed?: boolean }) => void
  onRemove: () => void
  onMove: (direction: 'up' | 'down') => void
  canMoveUp: boolean
  canMoveDown: boolean
}

function SubitemRow({
  subitem,
  onUpdate,
  onRemove,
  onMove,
  canMoveUp,
  canMoveDown,
}: SubitemRowProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState(subitem.text)

  const handleBlur = () => {
    setIsEditing(false)
    const trimmed = editText.trim()
    if (trimmed !== subitem.text) {
      onUpdate({ text: trimmed || 'Subitem' })
    }
    setEditText(subitem.text)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur()
    if (e.key === 'Escape') {
      setEditText(subitem.text)
      setIsEditing(false)
      e.currentTarget.blur()
    }
  }

  return (
    <li className={styles.subitem}>
      <input
        type="checkbox"
        checked={subitem.completed}
        onChange={(e) => onUpdate({ completed: e.target.checked })}
        className={styles.checkbox}
        aria-label={subitem.text || 'Subitem'}
      />
      {isEditing ? (
        <input
          type="text"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={styles.editInput}
          autoFocus
          aria-label="Editar subitem"
        />
      ) : (
        <span
          className={`${styles.subitemText} ${subitem.completed ? styles.completed : ''}`}
          onClick={() => {
            setIsEditing(true)
            setEditText(subitem.text)
          }}
          role="button"
          tabIndex={0}
        >
          {subitem.text || 'Subitem'}
        </span>
      )}
      <div className={styles.subitemActions}>
        <button
          type="button"
          onClick={() => onMove('up')}
          disabled={!canMoveUp}
          className={styles.iconBtn}
          aria-label="Subir"
        >
          <Icon icon="mdi:chevron-up" />
        </button>
        <button
          type="button"
          onClick={() => onMove('down')}
          disabled={!canMoveDown}
          className={styles.iconBtn}
          aria-label="Descer"
        >
          <Icon icon="mdi:chevron-down" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className={styles.iconBtnDanger}
          aria-label="Remover subitem"
        >
          <Icon icon="mdi:trash-can-outline" />
        </button>
      </div>
    </li>
  )
}
